import { CenterItem, Column, RowAlignedItems } from '@components/LinearLayout';
import {
  InputFieldsType,
  designTypeFields,
  orderedFields,
} from '@composites/InboundButton/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@components/Button';
import WeModal from '@components/WeModal';
import { ApplyButtonWrapper } from '@composites/Filters/styles';
import { DefaultBooleanValues } from '@composites/InboundButton';
import { getEventDetails } from '@store/slices/eventSlice';
import { SPEAKER_PAGE_WIDTH } from '@utils/constants';
import { safeJSONParse } from '@utils/fn';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import RegistrationMultiForm from '../RegistrationMultiForm';
import { useInbound } from '@utils/Inbound/useInbound';

interface WebappFreeTicketOnsiteProps {
  label: string;
  buttonType: string;
  eventId?: string;
  inboundValues?: {
    nonBooleanValues: Record<string, string>;
    booleanValues: DefaultBooleanValues;
  };
}

const WebappFreeTicketOnsite = ({
  label,
  inboundValues,
  buttonType,
  eventId,
}: WebappFreeTicketOnsiteProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const { inboundValue, actionInbound, registerInbound } = useInbound();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [checkedApproval, setCheckedApproval] = useState<boolean>(false);
  const [formsNumber, setFormsNumber] = useState<number>(0);

  const shouldShowPopup = useMemo(
    () => inboundValues?.nonBooleanValues?.['show-popup'] === 'true',
    [inboundValues?.nonBooleanValues]
  );

  const inputElements = useMemo(
    () =>
      orderedFields
        .map(item => {
          if (inboundValues?.booleanValues?.[item]?.visible) {
            const designTypeParameters = designTypeFields?.find(
              e => e.id === item
            );
            return {
              ...designTypeParameters,
              ...inboundValues?.booleanValues?.[item],
            };
          }
          return undefined;
        })
        ?.filter(item => item) as InputFieldsType,
    [inboundValues?.booleanValues]
  );

  const defaultValues: Record<string, string> = useMemo(() => {
    const element = new Array(formsNumber + 1).fill(0).map((a, i) => {
      if (i === 0)
        return {
          ...inputElements.reduce<Record<string, string>>((acc, key) => {
            acc[key.id] = safeJSONParse(inboundValue)?.[key.id] ?? '';
            return acc;
          }, {}),
          icebreaker: safeJSONParse(inboundValue)?.icebreaker
            ? safeJSONParse(inboundValue)?.icebreaker
            : 'false',
          dinner: safeJSONParse(inboundValue)?.dinner
            ? safeJSONParse(inboundValue)?.dinner
            : 'false',
        };
      else
        return {
          ...inputElements.reduce<Record<string, string>>((acc, key) => {
            acc[`${key.id}-${i}`] = safeJSONParse(inboundValue)?.[key.id] ?? '';
            return acc;
          }, {}),
          [`icebreaker-${i}`]: 'false',
          [`dinner-${i}`]: 'false',
        };
    });
    return element.reduce((acc, currentObj) => {
      return { ...acc, ...currentObj };
    }, {});
  }, [formsNumber, inboundValue, inputElements]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit = useCallback(
    async (data: Record<string, string>) => {
      if (!checkedApproval) return;

      await registerInbound(
        {
          ...data,
          event: sluggedEvent?.id,
          path: window.location.href,
          action: 'webapp_freeticket_onsite',
        },
        () => {
          setShowModal(false);
        }
      );
    },
    [checkedApproval, registerInbound, inboundValue, sluggedEvent?.id]
  );

  return (
    <div>
      {shouldShowPopup ? (
        <Button
          title={label}
          backgroundColor={sluggedEvent?.design?.themeColor}
          hoverBackgroundColor={theme.color.mobilityGrey}
          textColor={theme.color.white}
          lineHeight={theme.lineHeight.height125}
          fontSize={theme.fontSize.size20}
          borderRadius={theme.borderRadius.radius10}
          border={`0.1rem solid ${theme.color.mobilityGrey}`}
          padding="1.1rem 3rem"
          width="max-content"
          onClick={() => {
            setShowModal(true);
          }}
        />
      ) : (
        <Column>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <RegistrationMultiForm
              control={control}
              errors={errors}
              inputElements={inputElements}
              setCheckedApproval={setCheckedApproval}
              checkedApproval={checkedApproval}
              setFormsNumber={setFormsNumber}
              formsNumber={formsNumber}
              setValue={setValue}
              watch={watch}
            />
          </form>
          <Button
            title={label}
            backgroundColor={sluggedEvent?.design.themeColor}
            hoverBackgroundColor={theme.color.mobilityGrey}
            textColor={theme.color.white}
            borderRadius={theme.borderRadius.radius10}
            padding="0.6rem 2.5rem"
            width="max-content"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async () => {
              await handleSubmit(onSubmit)();
            }}
          />
        </Column>
      )}

      <WeModal
        visible={showModal}
        maxHeight="90vh"
        backgroundColor={theme.color.pureWhite}
        width={`min(${SPEAKER_PAGE_WIDTH}, 90vw)`}
        onClose={() => {
          setShowModal(false);
        }}
        footer={
          <CenterItem>
            <ApplyButtonWrapper>
              <RowAlignedItems $gap="3rem">
                <Button
                  title={t('save')}
                  backgroundColor={sluggedEvent?.design.themeColor}
                  hoverBackgroundColor={theme.color.mobilityGrey}
                  textColor={theme.color.white}
                  borderRadius={theme.borderRadius.radius10}
                  padding="0.6rem 2.5rem"
                  width="max-content"
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleSubmit(onSubmit)}
                />
              </RowAlignedItems>
            </ApplyButtonWrapper>
          </CenterItem>
        }
      >
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <RegistrationMultiForm
            control={control}
            errors={errors}
            inputElements={inputElements}
            setCheckedApproval={setCheckedApproval}
            checkedApproval={checkedApproval}
            setFormsNumber={setFormsNumber}
            formsNumber={formsNumber}
            setValue={setValue}
            watch={watch}
          />
        </form>
      </WeModal>
    </div>
  );
};

export default WebappFreeTicketOnsite;
